<script setup>
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useAlert } from 'dashboard/composables';
import { reactive, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import useVuelidate from '@vuelidate/core';
import {
  required,
  minLength,
  helpers,
  numeric,
  minValue,
  maxValue,
} from '@vuelidate/validators';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const { userInfo } = defineProps({
  userInfo: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const uiFlags = useMapGetter('giftCards/getUIFlags');
const banks = useMapGetter('giftCards/getBankList');
const creatingUserWithdraw = computed(() => uiFlags.value.creatingUserWithdraw);

const fetchBankState = ref(false);

// 表单数据
const state = reactive({
  bank_id: '',
  account_name: '',
  account_number: '',
  amount: 0,
  remarks: '',
});

const fetchBankList = async () => {
  await store.dispatch('giftCards/getBankList', {
    country_id: userInfo.country_id,
  });
  fetchBankState.value = true;
};

onMounted(() => {
  fetchBankList();
});

const bankOptions = computed(() =>
  banks.value.map(bank => ({
    value: String(bank.bank_category_id),
    label: bank.bank_name,
  }))
);

// 表单验证规则
const rules = {
  bank_id: {
    required: helpers.withMessage('Bank is required', required)
  },
  account_name: {
    required: helpers.withMessage('Account name is required', required),
    minLength: helpers.withMessage(
      'Account name must be at least 3 characters',
      minLength(3)
    ),
  },
  account_number: {
    required: helpers.withMessage('Account number is required', required),
    numeric: helpers.withMessage('Account number must be numeric', numeric),
  },
  amount: {
    required: helpers.withMessage('Amount is required', required),
    numeric: helpers.withMessage('Amount must be numeric', numeric),
    minValue: helpers.withMessage(
      'Amount must be at least 100',
      minValue(100)
    ),
    maxValue: helpers.withMessage(
      'Amount cannot exceed available balance',
      maxValue(computed(() => userInfo.money))
    ),
  },
  remarks: {},
};

const withdrawDialogRef = ref(null);
// 初始化 Vuelidate
const v$ = useVuelidate(rules, state);

// 提交表单逻辑
const handleSubmit = async () => {
  v$.value.$touch(); // 标记所有字段为“已接触”
  if (v$.value.$invalid) {
    return;
  }

  try {
    await store.dispatch('giftCards/userWithdraw', {
      user_id: userInfo.id,
      account_name: state.account_name,
      account_no: state.account_number,
      bank_name: state.bank_id,
      amount: state.amount,
      bank_id: state.bank_id,
      memo: state.remarks,
    });
    Object.assign(state, {
      bank_id: '',
      amount: 0,
      account_name: '',
      account_number: '',
      remarks: '',
    });
    withdrawDialogRef.value.close();
    useAlert('Withdraw success');
    await store.dispatch('giftCards/getMemberDetails', {
      user_id: userInfo.id,
    });
  } catch (error) {
    const errorMessage = parseAPIErrorResponse(error) || 'Create Withdraw error.';
    useAlert(errorMessage);
  } finally {
  }
};

const withdrawClose = () => {
  Object.assign(state, {
    amount: 0,
    bank_id: '',
    account_name: '',
    account_number: '',
    remarks: '',
  });
};

defineExpose({
  withdrawDialogRef,
});
</script>

<template>
  <Dialog
    ref="withdrawDialogRef"
    type="edit"
    title="Withdraw"
    overflow-y-auto
    width="md"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="withdrawClose"
  >
    <div class="space-y-4 bg-gray-50 p-4 rounded">
      <CardLayout layout="row">
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">User Id:</span>
            <span>{{ userInfo.id }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">Balance:</span>
            <span
              class="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full"
            >
              {{ userInfo.money }}
            </span>
          </div>
        </div>
      </CardLayout>
      <form class="space-y-3" @submit.prevent="handleSubmit">
        <ComboBox
          id="bank_id"
          v-model="state.bank_id"
          :options="bankOptions"
          placeholder="Select a bank"
          label="Bank"
          :message="v$.bank_id?.$errors[0]?.$message"
          :has-error="v$.bank_id?.$error"
        />
        <Input
          v-model="state.account_name"
          label="Account Name"
          placeholder="Enter Account Name"
          :message="v$.account_name.$errors[0]?.$message"
          :message-type="v$.account_name.$error ? 'error' : 'info'"
          dense
        />
        <Input
          v-model="state.account_number"
          label="Account Number"
          placeholder="Enter Account Number"
          type="number"
          :message="v$.account_number.$errors[0]?.$message"
          :message-type="v$.account_number.$error ? 'error' : 'info'"
          dense
        />
        <Input
          v-model="state.amount"
          label="Amount"
          placeholder="Enter Withdraw Amount"
          type="number"
          :message="v$.amount.$errors[0]?.$message"
          :message-type="v$.amount.$error ? 'error' : 'info'"
          dense
        />
        <Input
          v-model="state.remarks"
          label="Remarks"
          placeholder="Optional"
          dense
        />
        <Button
          type="submit"
          :is-loading="creatingUserWithdraw"
          :disabled="creatingUserWithdraw"
          size="sm"
          class="w-full"
        >
          Submit
        </Button>
      </form>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
/* 将内容块挤压得更紧凑 */
</style>
